import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../../HOC/Axios/Axios";
import * as yup from "yup";
import image from "../../Resources/Images/dummyImage.svg";
import Loader from "../../components/pageComponents/Spinner/loader";
import ScrollOff from "../../components/pageComponents/WindowScrollOff/ScrollOff";
import {
  IoClose,
  IoCloudUploadOutline,
  IoQrCodeOutline,
  IoWalletOutline,
  IoCheckmarkCircle,
  IoShieldCheckmarkOutline,
} from "react-icons/io5";
import { MdSave, MdInfo } from "react-icons/md";

function WalletSetting() {
  const [Images, setImages] = useState("");
  const [data, setData] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copied, setCopied] = useState(false);

  const walletSettingSchema = yup.object().shape({
    walletAddress: yup.string().required("Wallet address is required"),
  });

  const getData = async () => {
    setSpinner(true);
    try {
      const response = await axios.get("/company/getWalletInfo");
      if (response.status === 200 && response.data) setData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setSpinner(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSubmit = async (values, { resetForm }) => {
    const formData = new FormData();
    if (Images) {
      formData.append("depositQr", Images);
    } else if (data?.length > 0 && data[0]?.depositQr) {
      formData.append("depositQr", data[0].depositQr);
    }
    formData.append("walletAddress", values.walletAddress);
    setSpinner(true);
    try {
      const response = await axios.post("/company/postWalletInfo", formData);
      if (response.status === 201) {
        toast.success(response.data.message, { toastId: "wallet-success" });
        setImages("");
        getData();
        resetForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setSpinner(false);
    }
  };

  const handleRemoveImage = () => {
    setImages("");
    const input = document.querySelector("#qr-image");
    if (input) input.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) setImages(file);
  };

  const copyAddress = (addr) => {
    if (!addr) return;
    navigator.clipboard.writeText(addr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const currentQR = data?.[0]?.depositQr;
  const currentAddress = data?.[0]?.walletAddress;
  const hasData = data?.length > 0;

  return (
    <div className="min-h-screen bg-mainColor px-4 tablet:px-8 py-6 space-y-7">
      <ScrollOff message={spinner}>{spinner && <Loader />}</ScrollOff>
      <ToastContainer autoClose={1500} theme="colored" />

      {/* ── Page Hero ── */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 py-7 shadow-soft"
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #312e81 100%)",
        }}
      >
        <div
          className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
        />
        <div className="relative z-10 flex items-center gap-4">
          <div className="bg-secondMainColor/20 border border-secondMainColor/30 rounded-xl p-3">
            <IoWalletOutline className="text-violet-400 text-2xl" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Wallet Settings</h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Configure your USDT deposit wallet address and QR code
            </p>
          </div>
        </div>
      </div>

      {/* ── Status Badge (if data exists) ── */}
      {hasData && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
          <IoCheckmarkCircle className="text-emerald-500 text-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-emerald-700 font-semibold text-sm">Wallet configured</p>
            <p className="text-emerald-600 text-xs mt-0.5 truncate">
              Active address:{" "}
              <span className="font-mono">{currentAddress || "—"}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => copyAddress(currentAddress)}
            className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg
              bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      )}

      <Formik
        initialValues={{
          walletAddress: hasData ? data[0]?.walletAddress : "",
        }}
        onSubmit={handleSubmit}
        validationSchema={walletSettingSchema}
        enableReinitialize
      >
        {({ errors, touched }) => (
          <Form className="space-y-5">

            {/* ── Two-column grid on tablet+ ── */}
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-5 items-start">

              {/* Left — QR Code Upload Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                {/* Card Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
                  <div className="bg-violet-100 rounded-xl p-2">
                    <IoQrCodeOutline className="text-secondMainColor text-base" />
                  </div>
                  <div>
                    <h2 className="text-slate-800 font-bold text-sm">Deposit QR Code</h2>
                    <p className="text-slate-400 text-xs mt-0.5">
                      Shown to users during USDT deposit
                    </p>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-4">
                  {/* Hidden file input */}
                  <input
                    id="qr-image"
                    type="file"
                    accept="image/png, image/gif, image/jpeg"
                    onChange={(e) => setImages(e.target.files[0])}
                    className="hidden"
                  />

                  {/* Drop Zone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`relative rounded-xl overflow-hidden border-2 border-dashed transition-all duration-200
                      ${dragActive
                        ? "border-secondMainColor bg-secondMainColor/5 scale-[1.01]"
                        : "border-slate-200 bg-slate-50/60 hover:border-secondMainColor/40"
                      }`}
                    style={{ minHeight: "260px" }}
                  >
                    {Images ? (
                      <>
                        <img
                          src={URL.createObjectURL(Images)}
                          alt="QR Preview"
                          className="w-full h-[260px] object-contain bg-white p-3"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute top-3 right-3 bg-red-500 hover:bg-red-600
                            text-white p-1.5 rounded-full shadow-lg transition-colors"
                        >
                          <IoClose className="text-base" />
                        </button>
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                          <span className="bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                            New QR preview
                          </span>
                        </div>
                      </>
                    ) : currentQR ? (
                      <>
                        <img
                          src={currentQR}
                          alt="Current QR Code"
                          className="w-full h-[260px] object-contain bg-white p-3"
                        />
                        <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity
                          flex items-center justify-center">
                          <label
                            htmlFor="qr-image"
                            className="cursor-pointer text-white text-sm font-semibold flex flex-col items-center gap-2"
                          >
                            <IoCloudUploadOutline className="text-3xl" />
                            Click to replace
                          </label>
                        </div>
                      </>
                    ) : (
                      /* Empty state */
                      <label
                        htmlFor="qr-image"
                        className="flex flex-col items-center justify-center h-[260px] gap-3 cursor-pointer"
                      >
                        <div className="bg-secondMainColor/10 rounded-2xl p-5">
                          <IoQrCodeOutline className="text-secondMainColor text-4xl" />
                        </div>
                        <div className="text-center px-4">
                          <p className="text-slate-700 font-semibold text-sm">
                            Drop your QR code here
                          </p>
                          <p className="text-slate-400 text-xs mt-0.5">
                            or{" "}
                            <span className="text-secondMainColor underline underline-offset-2">
                              browse files
                            </span>
                          </p>
                          <p className="text-slate-300 text-xs mt-2">PNG, JPG, GIF supported</p>
                        </div>
                      </label>
                    )}
                  </div>

                  {/* Change button */}
                  <label htmlFor="qr-image" className="block">
                    <div
                      className="cursor-pointer flex items-center justify-center gap-2 text-sm font-semibold
                        text-secondMainColor border border-secondMainColor/30 bg-secondMainColor/5
                        hover:bg-secondMainColor hover:text-white px-4 py-2.5 rounded-xl transition-all duration-150"
                    >
                      <IoCloudUploadOutline className="text-base" />
                      {currentQR ? "Change QR Image" : "Upload QR Image"}
                    </div>
                  </label>
                </div>
              </div>

              {/* Right — Wallet Address Card + Tips */}
              <div className="space-y-5">

                {/* Wallet Address Card */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
                    <div className="bg-blue-100 rounded-xl p-2">
                      <IoWalletOutline className="text-blue-600 text-base" />
                    </div>
                    <div>
                      <h2 className="text-slate-800 font-bold text-sm">Wallet Address</h2>
                      <p className="text-slate-400 text-xs mt-0.5">
                        USDT (TRC-20 / ERC-20) deposit address
                      </p>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="walletAddress"
                        className="text-slate-700 font-semibold text-sm"
                      >
                        Wallet Address
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Field
                        id="walletAddress"
                        name="walletAddress"
                        type="text"
                        placeholder="e.g. TQn9Y2khDD95PF4hBVSPVSmASrjPGs7PJq"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60
                          text-slate-800 text-sm font-mono placeholder:text-slate-400 placeholder:font-sans
                          focus:outline-none focus:ring-2 focus:ring-secondMainColor/25
                          focus:border-secondMainColor focus:bg-white transition-all duration-150"
                      />
                      {errors.walletAddress && touched.walletAddress && (
                        <span className="text-red-500 text-xs px-1 flex items-center gap-1">
                          <MdInfo className="text-sm" />
                          {errors.walletAddress}
                        </span>
                      )}
                    </div>

                    {/* Current address preview */}
                    {currentAddress && (
                      <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                        <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1">
                          Currently Active
                        </p>
                        <p className="text-slate-700 text-xs font-mono break-all leading-relaxed">
                          {currentAddress}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tips Card */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
                    <div className="bg-amber-100 rounded-xl p-2">
                      <IoShieldCheckmarkOutline className="text-amber-600 text-base" />
                    </div>
                    <h2 className="text-slate-800 font-bold text-sm">Configuration Tips</h2>
                  </div>
                  <div className="px-5 py-4">
                    <ul className="space-y-3">
                      {[
                        "Double-check the wallet address before saving — transactions are irreversible.",
                        "Upload a clear, high-resolution QR code for easy scanning.",
                        "Use a dedicated deposit wallet to track incoming payments easily.",
                        "Supported networks: TRC-20, ERC-20 and BEP-20 USDT.",
                      ].map((tip, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondMainColor flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Submit */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5">
                  <div className="flex items-center gap-3">
                    <button
                      id="wallet-submit-btn"
                      type="submit"
                      className="inline-flex items-center gap-2 px-7 py-3 bg-sidebar
                        hover:bg-secondMainColor text-white font-semibold rounded-xl
                        shadow-md hover:shadow-glow transition-all duration-200 text-sm"
                    >
                      <MdSave className="text-lg" />
                      {hasData ? "Save Changes" : "Submit"}
                    </button>
                    <span className="text-slate-400 text-xs">
                      Changes take effect immediately
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default WalletSetting;
